import { useMyAccount } from '@ux/accounts-client';
import { useBrandId, useBrandConfig, useBrandLogos, useUrl, useUrls, useAppConfig } from '@ux/whitelabel';
import { useIsAuthenticated } from '@ux/auth';
import { useQueryClient, useMutation, useQuery } from 'react-query';
import { getCookie, eraseCookie, setCookie } from '@uds/cookies';
import { pipe, json, bodyOnly, fetch, Method } from '@ux/drivers';
import decode from 'jwt-decode';
import { useCallback, useMemo, createContext, useContext, Suspense, lazy } from 'react';
import { useSaveLanguage, useLanguage } from '@ux/language';
import { dispatchGAEvent } from '@uds/tagmg';
import { useSharedState } from '@ux/bus';
import { Row, Column, Container } from '@uds/grid';
import Colorway, { Colorways, AdditionalColorways } from '@uds/colorway';
import './footer.stylesheet_1cai24r.css';
import Message, { useMessage, Message as Message$1 } from '@uds/message';
import './footerlogo.stylesheet_1nwsfvw.css';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import Link from '@uds/link';
import './footerlinks.stylesheet_1tdonhv.css';
import { cx } from 'linaria';
import { classNames } from '@uds/typography';
import './footerlinkgroup.stylesheet_1k80bmr.css';
import './footerlink.stylesheet_1la25ae.css';
import './footerlegallinks.stylesheet_11hu4qw.css';
import { Glyph } from '@uds/glyphs';
import { cssTheme, breakpoints } from '@uds/theme-utils';
import './footersocial.stylesheet_1vs2jgr.css';
import PaddingBox from '@uds/padding-box';
import './footercopyright.stylesheet_1vs80r0.css';
import { createMedia } from '@artsy/fresnel';
import { useIntl } from 'react-intl';
import { useSupportedCurrencies } from '@ux/currency';
import CurrencySymbol from '@uds/currency-symbol';
import './currency.stylesheet_1ps9h4b.css';
import { useWindow } from '@uds/hooks';

const FETCH_BASKET = '@ux/bookends/basket';

const useRefreshBasketCount = () => {
  const queryCache = useQueryClient();
  const brandId = useBrandId();
  return useCallback(() => {
    /* 
      Normally you would want all parameters the fetch function depends on in the
      query-cache array. However in this case, if there is no cookie when this is called
      the first time, the count in the navbar won't update. There are no negative
      side effects of leaving it out in this case.
    */
    queryCache.invalidateQueries([FETCH_BASKET, brandId]);
  }, [queryCache, brandId]);
};

const useSetLanguage = () => {
  const {
    mutate: saveLanguage
  } = useSaveLanguage();
  return _ref => {
    let {
      key
    } = _ref;
    dispatchGAEvent({
      hitType: 'event',
      eventCategory: 'Custom',
      eventAction: 'click',
      eventLabel: 'bks-language-menu-item'
    });
    if (key != null) {
      saveLanguage({
        language: key.toString()
      });
    }
  };
};

const useHeaderHeightState = () => useSharedState('@ux/bookends/header-height', 0);
var useHeaderHeightState$1 = useHeaderHeightState;

var driver = pipe(json(), bodyOnly())(fetch);

const getBasketApiUrl = () => {
  try {
    return BASKET_API_URL;
  } catch (e) {
    return '/api/basket';
  }
};
var getBasketApiUrl$1 = getBasketApiUrl;

const getBasketId = () => {
  const basket = getCookie('basket');
  if (basket == null) {
    return null;
  }
  try {
    const {
      basketId
    } = decode(basket);
    return basketId;
  } catch {
    // if basket is not null, and decode fails, we have a basket uuid
    return basket;
  }
};
var getBasketId$1 = getBasketId;

const getBasket = brandId => {
  const url = `${getBasketApiUrl$1()}/api/v2/baskets/my`;
  const basketId = getBasketId$1();
  const basketHeader = basketId == null ? undefined : {
    'X-Basket': basketId
  };
  return driver({
    url,
    headers: {
      'X-Tenant': brandId,
      ...basketHeader
    }
  });
};
var getBasket$1 = getBasket;

const assignBasket = (basketId, brandId) => {
  const url = `${getBasketApiUrl$1()}/api/v2/baskets/${basketId}/customer`;
  return driver({
    url,
    method: Method.POST,
    headers: {
      'X-Tenant': brandId
    }
  });
};
var assignBasket$1 = assignBasket;

const useAssignBasket = () => {
  const brandId = useBrandId();
  return useMutation(basketId => assignBasket$1(basketId, brandId));
};
var useAssignBasket$1 = useAssignBasket;

const useHasAccount = () => {
  const {
    isSuccess,
    data
  } = useMyAccount();
  return Boolean(isSuccess) && Boolean(data);
};
const useLogos = () => {
  const {
    logoContrast,
    logoDark,
    logoLight
  } = useBrandLogos();
  return {
    logoContrast,
    logoDark,
    logoLight,
    contrast: logoContrast,
    dark: logoDark,
    light: logoLight
  };
};
const useBasketUrl = () => useUrl('basket');
const useLoginUrl = () => useUrl('sso.login');
const useRegisterUrl = () => useUrl('sso.register');
const useAccountUrl = () => useUrl('accountManagement.index');
const useHomeUrl = () => useUrl('storefront.homepage');
const useControlPanelUrl = () => useUrl('controlPanel.index');
const useBasket = () => {
  const basketId = getBasketId$1();
  const isAuthenticated = useIsAuthenticated();
  const brandId = useBrandId();
  const {
    mutate: assignBasket
  } = useAssignBasket$1();
  return useQuery(
  /* 
    Normally you would want all parameters the fetch function depends on in the
    query-cache array. However in this case, if there is no cookie when this is called
    the first time, the count in the navbar won't update. There are no negative
    side effects of leaving it out in this case.
  */
  [FETCH_BASKET, brandId], () => getBasket$1(brandId), {
    enabled: basketId != null || isAuthenticated,
    onError: error => {
      const isPPEError = error => {
        if ('httpStatusCode' in error) {
          return error.httpStatusCode === 400;
        }
        return false;
      };
      const isBasketServiceError = error => {
        if ('code' in error && error.code != null) {
          return [400, 401, 403, 404].includes(error.code);
        }
        return false;
      };
      if (isPPEError(error) || isBasketServiceError(error)) {
        eraseCookie('basket');
      }
    },
    onSuccess: basket => {
      var _basket$customer, _basket$jwtBasketToke;
      const isAnonymousBasket = !(basket !== null && basket !== void 0 && (_basket$customer = basket.customer) !== null && _basket$customer !== void 0 && _basket$customer.registered);
      const basketCookie = (_basket$jwtBasketToke = basket === null || basket === void 0 ? void 0 : basket.jwtBasketToken) != null ? _basket$jwtBasketToke : basket === null || basket === void 0 ? void 0 : basket.id;
      if (isAuthenticated && isAnonymousBasket) {
        assignBasket(basketId);
      }
      if (isAuthenticated && basketId == null && basketCookie != null) {
        setCookie('basket', basketCookie);
      }
    }
  });
};
const useBasketCount = () => {
  var _query$data$items, _query$data;
  const query = useBasket();
  const count = ((_query$data$items = (_query$data = query.data) === null || _query$data === void 0 ? void 0 : _query$data.items) != null ? _query$data$items : []).filter(item => item.boltOnReferencedItemId == null || item.boltOnReferencedItemId === 0).reduce((acc, item) => {
    var _item$quantity;
    return acc + ((_item$quantity = item.quantity) != null ? _item$quantity : 0);
  }, 0);
  return {
    ...query,
    data: count
  };
};
const useIsEcommerce = () => {
  var _config$ecommerce;
  const config = useBrandConfig();
  return (_config$ecommerce = config.ecommerce) != null ? _config$ecommerce : true;
};

const root$5 = "r1l0uwht";
const logo = "l1m6y7v3";
const additional = "a18m49ai";
const classnames$5 = {
  root: root$5,
  logo,
  additional
};

const HeaderColorways = {
  ...Colorways,
  ...AdditionalColorways
};
let GlyphAlignment;
(function (GlyphAlignment) {
  GlyphAlignment["START"] = "START";
  GlyphAlignment["END"] = "END";
})(GlyphAlignment || (GlyphAlignment = {}));
let Menu;
(function (Menu) {
  Menu["NAV"] = "@ux/bookends/nav-menu";
  Menu["ACCOUNT"] = "@ux/bookends/account-menu";
  Menu["CONTACT"] = "@ux/bookends/contact-menu";
  Menu["SUPPLEMENTARY"] = "@ux/bookends/supplementary-menu";
})(Menu || (Menu = {}));

const BookendsContext = /*#__PURE__*/createContext(undefined);
const isAbsoluteUrl = function () {
  let path = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '/';
  const absoluteUrlRegex = /^[a-z][a-z\d+.-]*:/;
  return absoluteUrlRegex.test(path);
};
const checkExternalRoutes = _ref => {
  let {
    absolutePath,
    externalRoutes
  } = _ref;
  const baseURI = document.baseURI;
  // make routes absolute to check between them
  const includedInExternal = externalRoutes.some(route => {
    const absoluteRoute = isAbsoluteUrl(route) ? route : new URL(route, baseURI).href;
    return absoluteRoute === absolutePath;
  });
  return includedInExternal;
};
const isLastCharSlash = path => {
  return (path === null || path === void 0 ? void 0 : path.charAt(path.length - 1)) === '/';
};
const trimLastChar = path => {
  return path.substring(0, path.length - 1);
};
const isExternal = _ref2 => {
  let {
    path,
    externalRoutes
  } = _ref2;
  let mappedExternalRoutes = [...externalRoutes];
  if (isLastCharSlash(path)) {
    mappedExternalRoutes = mappedExternalRoutes.map(route => isLastCharSlash(route) ? route : `${route}/`);
  } else {
    mappedExternalRoutes = mappedExternalRoutes.map(route => isLastCharSlash(route) ? trimLastChar(route) : route);
  }
  if (path != null && typeof path === 'string') {
    const baseURI = document.baseURI;
    const absolutePath = isAbsoluteUrl(path) ? path : new URL(path, baseURI).href;
    const isNotBaseMatched = baseURI !== `${new URL(absolutePath).origin}/`;
    const includedInExternal = checkExternalRoutes({
      absolutePath,
      externalRoutes: mappedExternalRoutes
    });
    const isAbsolute = isAbsoluteUrl(path);
    const isTel = path.startsWith('tel:');
    const isMail = path.startsWith('mailto:');
    const isNotLink = isTel || isMail;
    // if document origin and path origin do not match, surely external
    // if its included in external links, its external
    // if we have no external links and its an absolute url, its external
    // if its not even a link, its definitely external
    return isNotBaseMatched || includedInExternal || isAbsolute && externalRoutes.length === 0 || isNotLink;
  }
  return false;
};
const BookendsProvider = _ref3 => {
  let {
    children,
    colorway,
    colorwayAgainst,
    colorwaySecondary,
    LinkComponent,
    externalRoutes = [],
    languageMap,
    onLanguageChange
  } = _ref3;
  const isExternalLink = useCallback(path => isExternal({
    path,
    externalRoutes
  }), [externalRoutes]);
  const state = useMemo(() => ({
    onLanguageChange,
    colorway,
    colorwayAgainst,
    colorwaySecondary,
    LinkComponent: _ref4 => {
      let {
        href,
        ...props
      } = _ref4;
      const isExternalRoute = isExternalLink(href);
      if (isExternalRoute) {
        return /*#__PURE__*/jsx(Link, {
          ...props,
          href: href
        });
      }
      const parsedHref = isAbsoluteUrl(href) ? new URL(href != null ? href : '/').pathname : href;
      return /*#__PURE__*/jsx(LinkComponent, {
        ...props,
        href: parsedHref
      });
    },
    externalRoutes,
    languageMap,
    isExternalLink
  }), [colorway, colorwayAgainst, colorwaySecondary, LinkComponent, externalRoutes, languageMap, isExternalLink, onLanguageChange]);
  return /*#__PURE__*/jsx(BookendsContext.Provider, {
    value: state,
    children: children
  });
};
const useBookends = () => {
  const context = useContext(BookendsContext);
  if (context === undefined) {
    throw new Error('The context is undefined, did you forget to add BookendsProvider?');
  }
  return context;
};

const classname$1 = "cja3jce";

const normalizeLinkProp = function () {
  let language = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  let prop = arguments.length > 1 ? arguments[1] : undefined;
  return props => {
    if (props[`${prop}s`] != null) {
      var _map$get;
      const map = new Map(Object.entries(props[`${prop}s`]));
      const value = (_map$get = map.get(language)) != null ? _map$get : props[prop];
      const {
        [`${prop}s`]: notUsed,
        ...rest
      } = props;
      return {
        ...rest,
        [prop]: value
      };
    }
    return props;
  };
};
const normalizeLinkUrl = language => normalizeLinkProp(language, 'url');

const useFooterConfig = () => useAppConfig('bks').sales.footer;
const useLogo = useBrandLogos;
const useLinkGroups = () => {
  var _useFooterConfig;
  return (_useFooterConfig = useFooterConfig()) === null || _useFooterConfig === void 0 ? void 0 : _useFooterConfig.groups;
};
const createLink = (label, toProp, language) => urls => {
  var _ref, _urls;
  const url = (_ref = (_urls = urls[`${language}.${toProp}`]) != null ? _urls : urls[`${toProp}.${language}`]) != null ? _ref : urls[toProp];
  return {
    href: url,
    label
  };
};
const useLegalLinks = (urls, language) => {
  var _ref2;
  const urlsDefault = useUrls();
  const urlsData = urls != null ? urls : urlsDefault;
  const {
    data
  } = useLanguage();
  const lang = (_ref2 = language != null ? language : data) != null ? _ref2 : '';
  return [createLink('legal.imprint', 'legal.imprint', lang)(urlsData), createLink('legal.terms', 'legal.terms', lang)(urlsData), createLink('legal.privacy', 'legal.privacy', lang)(urlsData), createLink('legal.cookies', 'cookieManagement', lang)(urlsData)].filter(link => link.href);
};
const useComplementaryLinks = (config, language) => {
  var _ref3, _configData$complemen, _configData$complemen2, _configData$complemen3;
  const configDefault = useFooterConfig();
  const configData = config != null ? config : configDefault;
  const {
    data
  } = useLanguage();
  const lang = (_ref3 = language != null ? language : data) != null ? _ref3 : '';
  return ((_configData$complemen = configData === null || configData === void 0 ? void 0 : (_configData$complemen2 = configData.complementary) === null || _configData$complemen2 === void 0 ? void 0 : (_configData$complemen3 = _configData$complemen2.menu) === null || _configData$complemen3 === void 0 ? void 0 : _configData$complemen3.items) != null ? _configData$complemen : []).map(normalizeLinkUrl(lang)).map(_ref4 => {
    let {
      url,
      ...rest
    } = _ref4;
    return {
      href: url,
      ...rest
    };
  }).filter(link => link.href).slice(0, 10);
};
const useSocialLinks = () => {
  var _useBrandConfig$socia;
  return (_useBrandConfig$socia = useBrandConfig().social) != null ? _useBrandConfig$socia : {};
};

const FooterLogo = () => {
  const {
    colorway
  } = useBookends();
  const logo = useLogo();
  const logoUrl = colorway === Colorways.LIGHT || colorway === Colorways.NEUTRAL ? logo.logoLight : logo.logoDark;
  return /*#__PURE__*/jsx("img", {
    src: logoUrl,
    alt: useMessage({
      id: 'brand.legal.displayName'
    }),
    className: classname$1
  });
};
FooterLogo.displayName = 'FooterLogo';
var FooterLogo$1 = FooterLogo;

const classname = "c1kwexx1";

const root$4 = "rznt0ci";
const title = "t122iau1";
const list = "lq85cln";
const listItem$1 = "l17049be";
const classnames$4 = {
  root: root$4,
  title: cx(classNames.headingXxs, title),
  list,
  listItem: listItem$1
};

const root$3 = "r1ro1xl";
const classnames$3 = {
  root: cx(classNames.content, root$3)
};

const FooterLink = _ref => {
  let {
    children,
    href
  } = _ref;
  const {
    LinkComponent
  } = useBookends();
  return /*#__PURE__*/jsx(LinkComponent, {
    className: classnames$3.root,
    href: href,
    children: children
  });
};
FooterLink.displayName = 'FooterLink';
var FooterLink$1 = FooterLink;

const FooterLinkGroup = _ref => {
  let {
    title,
    links
  } = _ref;
  return /*#__PURE__*/jsxs("div", {
    className: classnames$4.root,
    children: [/*#__PURE__*/jsx("h2", {
      className: classnames$4.title,
      children: /*#__PURE__*/jsx(Message, {
        id: title
      })
    }), /*#__PURE__*/jsx("ul", {
      className: classnames$4.list,
      children: links.map(link => /*#__PURE__*/jsx("li", {
        className: classnames$4.listItem,
        children: /*#__PURE__*/jsx(FooterLink$1, {
          href: link.href,
          children: /*#__PURE__*/jsx(Message, {
            id: link.label
          })
        })
      }, link.label))
    })]
  });
};
FooterLinkGroup.displayName = 'FooterLinkGroup';
var FooterLinkGroup$1 = FooterLinkGroup;

const FooterLinks = () => {
  const linkGroups = useLinkGroups();
  return /*#__PURE__*/jsx(Fragment, {
    children: Boolean(linkGroups) && linkGroups.length > 0 && /*#__PURE__*/jsx("section", {
      className: classname,
      children: /*#__PURE__*/jsx(Row, {
        children: linkGroups.map(group => /*#__PURE__*/jsx(Column, {
          xs: 12,
          sm: 6,
          lg: 3,
          children: /*#__PURE__*/jsx(FooterLinkGroup$1, {
            ...group
          })
        }, group.title))
      })
    })
  });
};
FooterLinks.displayName = 'FooterLinks';
var FooterLinks$1 = FooterLinks;

const root$2 = "r1yk2epq";
const listItem = "le4gs32";
const classnames$2 = {
  root: root$2,
  listItem
};

const FooterLegalLinks = () => {
  const legalLinks = useLegalLinks();
  const complementaryLinks = useComplementaryLinks();
  return /*#__PURE__*/jsxs("ul", {
    className: classnames$2.root,
    children: [legalLinks.map((link, index) => {
      var _link$key;
      return /*#__PURE__*/jsx("li", {
        className: classnames$2.listItem,
        children: /*#__PURE__*/jsx(FooterLink$1, {
          href: link.href,
          children: /*#__PURE__*/jsx(Message, {
            cascade: [{
              id: `bks.footer.${link.label}`
            }, {
              id: `brand.footer.${link.label}`
            }]
          })
        })
      }, (_link$key = link.key) != null ? _link$key : index);
    }), complementaryLinks.map((link, index) => {
      var _link$key2, _link$key3;
      return /*#__PURE__*/jsx("li", {
        className: classnames$2.listItem,
        children: /*#__PURE__*/jsx(FooterLink$1, {
          href: link.href,
          children: /*#__PURE__*/jsx(Message, {
            id: `bks.footer.complementary.menu.items.${(_link$key3 = link.key) != null ? _link$key3 : index}.label`
          })
        })
      }, (_link$key2 = link.key) != null ? _link$key2 : index);
    })]
  });
};
FooterLegalLinks.displayName = 'FooterLegalLinks';
var FooterLegalLinks$1 = FooterLegalLinks;

const root$1 = "rr4fdup";
const icon = "itkvfa4";
const getClassNames = _ref => {
  let {
    colorway = HeaderColorways.DARK
  } = _ref;
  return {
    root: root$1,
    icon,
    iconColor: colorway === HeaderColorways.LIGHT || colorway === HeaderColorways.NEUTRAL ? cssTheme.palette.grey02 : '#fff'
  };
};

const FooterSocial = () => {
  const {
    colorway
  } = useBookends();
  const classNames = getClassNames({
    colorway
  });
  const {
    facebook,
    twitter
  } = useSocialLinks();
  return facebook != null || twitter != null ? /*#__PURE__*/jsxs("div", {
    className: classNames.root,
    children: [facebook && /*#__PURE__*/jsx("a", {
      className: classNames.icon,
      href: `https://facebook.com/${facebook}`,
      target: "_blank",
      rel: "noopener noreferrer",
      id: "bks-social-facebook",
      children: /*#__PURE__*/jsx(Glyph, {
        name: "socialFacebook",
        alt: "facebook",
        color: classNames.iconColor
      })
    }), twitter && /*#__PURE__*/jsx("a", {
      className: classNames.icon,
      href: `https://twitter.com/${twitter}`,
      target: "_blank",
      rel: "noopener noreferrer",
      id: "bks-social-twitter",
      children: /*#__PURE__*/jsx(Glyph, {
        name: "socialTwitter",
        alt: "Twitter",
        color: classNames.iconColor
      })
    })]
  }) : null;
};
FooterSocial.displayName = 'FooterSocial';
var FooterSocial$1 = FooterSocial;

const text = "t17vhnwo";
const classnames$1 = {
  text: cx(classNames.smallprint, text)
};

const FooterCopyright = () => /*#__PURE__*/jsx(PaddingBox, {
  children: /*#__PURE__*/jsx("small", {
    className: classnames$1.text,
    children: /*#__PURE__*/jsx(Message$1, {
      cascade: [{
        id: 'bks.footer.legal.copyright'
      }, {
        id: 'brand.legal.copyright'
      }],
      values: {
        year: new Date().getFullYear()
      }
    })
  })
});
FooterCopyright.displayName = 'FooterCopyright';
var FooterCopyright$1 = FooterCopyright;

const Footer = _ref => {
  let {
    colorway = HeaderColorways.DARK,
    LinkComponent,
    externalRoutes
  } = _ref;
  return /*#__PURE__*/jsx(BookendsProvider, {
    colorway: colorway,
    LinkComponent: LinkComponent,
    externalRoutes: externalRoutes,
    children: /*#__PURE__*/jsx(Colorway, {
      colorway: colorway,
      children: /*#__PURE__*/jsxs("footer", {
        className: classnames$5.root,
        children: [/*#__PURE__*/jsxs(Container, {
          children: [/*#__PURE__*/jsx("div", {
            className: classnames$5.logo,
            children: /*#__PURE__*/jsx(FooterLogo$1, {})
          }), /*#__PURE__*/jsx(FooterLinks$1, {})]
        }), /*#__PURE__*/jsx("section", {
          className: classnames$5.additional,
          children: /*#__PURE__*/jsxs(Container, {
            children: [/*#__PURE__*/jsx(FooterLegalLinks$1, {}), /*#__PURE__*/jsx(FooterSocial$1, {}), /*#__PURE__*/jsx(FooterCopyright$1, {})]
          })
        })]
      })
    })
  });
};
Footer.displayName = 'Footer';
var Footer$1 = Footer;

const root = "rzcnx3f";
const symbol = "s1d6gxrz";
const classnames = {
  root,
  symbol
};

const Currency = _ref => {
  let {
    currency
  } = _ref;
  return /*#__PURE__*/jsxs("span", {
    className: classnames.root,
    children: [/*#__PURE__*/jsx(Message$1, {
      id: `bks.currency.label.${currency.toLowerCase()}`,
      values: {
        currencySymbol: null
      }
    }), /*#__PURE__*/jsx("span", {
      className: classnames.symbol,
      children: /*#__PURE__*/jsx(CurrencySymbol, {
        currency: currency
      })
    })]
  });
};
Currency.displayName = 'Currency';
var Currency$1 = Currency;

const hasCurrencyPicker = _ref => {
  let {
    currencies,
    loggedIn,
    accountFetched,
    accountCurrency
  } = _ref;
  if (currencies.length > 1 && (loggedIn !== true || accountFetched === true && accountCurrency == null)) {
    return true;
  }
  return false;
};
const getCurrencyPickerLinks = _ref2 => {
  let {
    currencies,
    intl
  } = _ref2;
  return currencies.map(currency => {
    return {
      key: currency,
      label: /*#__PURE__*/jsx(Currency$1, {
        currency: currency
      }),
      option: intl ? intl.formatMessage({
        id: `bks.currency.label.${currency.toLowerCase()}`
      }, {
        currencySymbol: intl.formatNumber(0, {
          style: 'currency',
          currency: currency,
          maximumSignificantDigits: 1
        }).replace('0', '')
      }) : currency
    };
  });
};
const getCurrentCurrency = _ref3 => {
  var _links$find;
  let {
    links,
    currentCurrency
  } = _ref3;
  return (_links$find = links.find(link => link.key === currentCurrency)) != null ? _links$find : links[0];
};

const useBookendsConfig = () => useAppConfig('bks');
const useHelpUrl = (customConfig, language) => {
  var _ref;
  const defaultConfig = useBrandConfig();
  const config = customConfig != null ? customConfig : defaultConfig;
  const {
    data: lang
  } = useLanguage();
  const currentLang = (_ref = language != null ? language : lang) != null ? _ref : '';
  const normalize = normalizeLinkUrl(currentLang);
  const support = config.support;
  return normalize(support).url;
};
const useLinks = (customConfig, language) => {
  var _ref2, _config$sales$header$, _config$sales2, _config$sales2$header;
  const {
    data: lang
  } = useLanguage();
  const currentLang = (_ref2 = language != null ? language : lang) != null ? _ref2 : '';
  const defaultConfig = useBookendsConfig();
  const config = customConfig != null ? customConfig : defaultConfig;
  const normalize = normalizeLinkUrl(currentLang);
  return ((_config$sales$header$ = config === null || config === void 0 ? void 0 : (_config$sales2 = config.sales) === null || _config$sales2 === void 0 ? void 0 : (_config$sales2$header = _config$sales2.header) === null || _config$sales2$header === void 0 ? void 0 : _config$sales2$header.menu) != null ? _config$sales$header$ : []).map(normalize).map(menuItem => {
    var _menuItem$submenu$ite, _menuItem$submenu;
    if (!menuItem.submenu) {
      return menuItem;
    }
    return {
      ...menuItem,
      submenu: {
        ...menuItem.submenu,
        items: ((_menuItem$submenu$ite = menuItem === null || menuItem === void 0 ? void 0 : (_menuItem$submenu = menuItem.submenu) === null || _menuItem$submenu === void 0 ? void 0 : _menuItem$submenu.items) != null ? _menuItem$submenu$ite : []).map(item => normalize(item))
      }
    };
  });
};
const useAccountLinks = (customConfig, language) => {
  var _ref3, _config$sales$header$2, _config$sales3, _config$sales3$header, _config$sales3$header2, _config$sales3$header3;
  const {
    data: lang
  } = useLanguage();
  const currentLang = (_ref3 = language != null ? language : lang) != null ? _ref3 : '';
  const defaultConfig = useBookendsConfig();
  const config = customConfig != null ? customConfig : defaultConfig;
  return ((_config$sales$header$2 = config === null || config === void 0 ? void 0 : (_config$sales3 = config.sales) === null || _config$sales3 === void 0 ? void 0 : (_config$sales3$header = _config$sales3.header) === null || _config$sales3$header === void 0 ? void 0 : (_config$sales3$header2 = _config$sales3$header.account) === null || _config$sales3$header2 === void 0 ? void 0 : (_config$sales3$header3 = _config$sales3$header2.menu) === null || _config$sales3$header3 === void 0 ? void 0 : _config$sales3$header3.items) != null ? _config$sales$header$2 : []).map(normalizeLinkUrl(currentLang));
};
const useSupplementaryLinks = (customConfig, language) => {
  var _ref4, _config$sales$header$3, _config$sales4, _config$sales4$header, _config$sales4$header2, _config$sales4$header3;
  const {
    data: lang
  } = useLanguage();
  const currentLang = (_ref4 = language != null ? language : lang) != null ? _ref4 : '';
  const defaultConfig = useBookendsConfig();
  const config = customConfig != null ? customConfig : defaultConfig;
  return ((_config$sales$header$3 = config === null || config === void 0 ? void 0 : (_config$sales4 = config.sales) === null || _config$sales4 === void 0 ? void 0 : (_config$sales4$header = _config$sales4.header) === null || _config$sales4$header === void 0 ? void 0 : (_config$sales4$header2 = _config$sales4$header.supplementary) === null || _config$sales4$header2 === void 0 ? void 0 : (_config$sales4$header3 = _config$sales4$header2.menu) === null || _config$sales4$header3 === void 0 ? void 0 : _config$sales4$header3.items) != null ? _config$sales$header$3 : []).slice(0, 5).map(normalizeLinkUrl(currentLang));
};
const useContactLinks = (customConfig, language) => {
  var _ref5, _config$sales$header$4, _config$sales5, _config$sales5$header, _config$sales5$header2, _config$sales5$header3;
  const {
    data: lang
  } = useLanguage();
  const currentLang = (_ref5 = language != null ? language : lang) != null ? _ref5 : '';
  const defaultConfig = useBookendsConfig();
  const config = customConfig != null ? customConfig : defaultConfig;
  return ((_config$sales$header$4 = config === null || config === void 0 ? void 0 : (_config$sales5 = config.sales) === null || _config$sales5 === void 0 ? void 0 : (_config$sales5$header = _config$sales5.header) === null || _config$sales5$header === void 0 ? void 0 : (_config$sales5$header2 = _config$sales5$header.contact) === null || _config$sales5$header2 === void 0 ? void 0 : (_config$sales5$header3 = _config$sales5$header2.menu) === null || _config$sales5$header3 === void 0 ? void 0 : _config$sales5$header3.items) != null ? _config$sales$header$4 : []).map(normalizeLinkUrl(currentLang));
};
const useShortcuts = controlPanelUrl => {
  const defaultUrl = useControlPanelUrl();
  const url = controlPanelUrl != null ? controlPanelUrl : defaultUrl;

  // Urls may not be set on non-ecommerce brands so we must filter them out
  return [{
    label: 'bks.masthead.controlPanel',
    url,
    icon: 'controls',
    analyticsKey: 'control-panel'
  }].filter(_ref6 => {
    let {
      url
    } = _ref6;
    return Boolean(url);
  });
};
const useShowMenu = function (links) {
  let languageMap = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const contactLinks = useContactLinks();
  const supplementaryLinks = useSupplementaryLinks();
  const shortcutNav = useShortcuts();
  const isEcommerce = useIsEcommerce();
  const showLanguagePicker = Object.values(languageMap).length > 1;
  const {
    data: account,
    isSuccess
  } = useMyAccount();
  const showCurrencyPicker = hasCurrencyPicker({
    currencies: useSupportedCurrencies(),
    loggedIn: useIsAuthenticated(),
    accountCurrency: account === null || account === void 0 ? void 0 : account.currency,
    accountFetched: isSuccess
  });
  return links.length > 0 || supplementaryLinks.length > 0 || contactLinks.length > 0 || shortcutNav.length > 0 || isEcommerce || showLanguagePicker || showCurrencyPicker;
};

/**
 * If the menu item has `promo`
 * add the promo label using cascading intl message
 */
const addPromoLabel = intl => link => {
  if (!(link !== null && link !== void 0 && link.promo)) {
    return link;
  }
  const promoLabel = link === null || link === void 0 ? void 0 : link.promoLabel;
  if (promoLabel != null) {
    link.promoLabel = intl.messages[promoLabel] != null ? promoLabel : 'bks.menu.promo';
    return link;
  }
  link.promoLabel = 'bks.menu.promo';
  return link;
};

/**
 * If the submenu has `illustrationKey`
 * add the title & body labels using cascading intl message
 */
const addSubmenuLabels = label => link => {
  if ((link === null || link === void 0 ? void 0 : link.illustrationKey) != null) {
    link.title = `${label}.title`;
    link.body = `${label}.body`;
  }
  return link;
};

/**
 * We offer a maximum of 9 top menu messages, and 81 submenu messages
 * as each top menu item can contain 9 submenu items
 *
 * The top level menu items use the lexicon IDs
 *  bks.menu.items.[0-8].label
 *
 * The submenu items use the lexicon IDs
 *  bks.menu.submenu.items.[0-80].title
 *  bks.menu.submenu.items.[0-80].body
 *
 * Rather than using an array index, each of the top menu and submenu items
 * contain a `key` from brandpilot:
 * 0-9 for top level items, 0-80 for submenu items
 *
 * This means the menu can be easily re-ordered within bpt
 * by adjusting the order of the yaml
 */

const formatLabels = _ref => {
  let {
    links,
    intl,
    externalConfig
  } = _ref;
  return links.map((link, indexLink) => {
    var _link$key, _result, _result$submenu$items, _result2, _result2$submenu, _link$key2, _result3;
    let result = {
      ...link,
      label: externalConfig ? link.label : `bks.menu.items.${(_link$key = link.key) != null ? _link$key : indexLink}.label`
    };
    result = addPromoLabel(intl)(result);
    result = {
      ...result,
      submenu: {
        ...((_result = result) === null || _result === void 0 ? void 0 : _result.submenu),
        items: ((_result$submenu$items = (_result2 = result) === null || _result2 === void 0 ? void 0 : (_result2$submenu = _result2.submenu) === null || _result2$submenu === void 0 ? void 0 : _result2$submenu.items) != null ? _result$submenu$items : []).map((item, indexItem) => {
          var _item$key, _item$key2;
          const result = {
            ...item,
            title: `bks.menu.submenu.items.${(_item$key = item.key) != null ? _item$key : indexItem}.title`,
            body: `bks.menu.submenu.items.${(_item$key2 = item.key) != null ? _item$key2 : indexItem}.body`
          };
          return addPromoLabel(intl)(result);
        })
      }
    };
    return {
      ...result,
      submenu: addSubmenuLabels(`bks.menu.items.${(_link$key2 = link.key) != null ? _link$key2 : indexLink}.submenu`)((_result3 = result) === null || _result3 === void 0 ? void 0 : _result3.submenu)
    };
  });
};

const useLocation = () => useWindow().location;
const useOrigin = () => useLocation().origin;

/**  Returns the location pathname  */
const usePathname = () => useLocation().pathname;

/**
 * Returns the concatenation of the location pathname and hash
 *   Input: Object => location: { pathname: '/foo', hash: '#bar' }
 *   Output: string => '/foo#bar'
 */
const useFullPath = () => {
  const location = useLocation();
  return `${location.pathname}${location.hash}`;
};

/**
 * Returns the item of menu if one of submenu item url matches the current url
 *   Input: Object[] => [{ url: '/', submenu: { items: { url: '/foo/' }}}]
 *   Output: number => 0
 */
const useMenuItemActiveIndex = links => {
  const origin = useOrigin();
  const pathname = usePathname();
  const fullPath = useFullPath();
  const paths = [pathname, fullPath, `${origin}${pathname}`, `${origin}${fullPath}`];
  return links.findIndex(link => {
    var _commonPropsLink$subm;
    const modelCommonLink = {
      url: '',
      submenu: {
        items: [{
          url: ''
        }]
      }
    };
    const commonPropsLink = Object.assign(modelCommonLink, link);
    return ((_commonPropsLink$subm = commonPropsLink.submenu) === null || _commonPropsLink$subm === void 0 ? void 0 : _commonPropsLink$subm.items.find(item => {
      return paths.find(path => item.url !== '' && path === item.url) != null;
    })) != null;
  });
};

const {
  MediaContextProvider,
  Media
} = createMedia({
  breakpoints: {
    xs: breakpoints.xs,
    md: breakpoints.md
  }
});
const HeaderDesktop = /*#__PURE__*/lazy(() => import('./HeaderDesktop-7f4999b7.js'));
const HeaderMobile = /*#__PURE__*/lazy(() => import('./HeaderMobile-4d316954.js'));
const Header = props => {
  var _props$links;
  const configLinks = useLinks();
  const links = formatLabels({
    links: (_props$links = props.links) != null ? _props$links : configLinks,
    intl: useIntl(),
    externalConfig: props.links != null
  });
  const menuItemActiveIndex = useMenuItemActiveIndex(links);
  return (
    /*#__PURE__*/
    // @ts-ignore: error after upgrading to react 18
    jsxs(MediaContextProvider, {
      children: [/*#__PURE__*/jsx(Media, {
        at: "xs",
        children: /*#__PURE__*/jsx(Suspense, {
          fallback: "",
          children: /*#__PURE__*/jsx(HeaderMobile, {
            ...props,
            links: links,
            menuItemActiveIndex: menuItemActiveIndex
          })
        })
      }), /*#__PURE__*/jsx(Media, {
        greaterThanOrEqual: "md",
        children: /*#__PURE__*/jsx(Suspense, {
          fallback: "",
          children: /*#__PURE__*/jsx(HeaderDesktop, {
            ...props,
            links: links,
            menuItemActiveIndex: menuItemActiveIndex
          })
        })
      })]
    })
  );
};
Header.displayName = 'Header';
var Header$1 = Header;

const SalesHeader = _ref => {
  let {
    LinkComponent,
    colorway = HeaderColorways.HEADER,
    colorwayAgainst = HeaderColorways.DARK,
    colorwaySecondary = HeaderColorways.LIGHT,
    externalRoutes,
    languageMap,
    links,
    onLanguageChange
  } = _ref;
  return /*#__PURE__*/jsx(BookendsProvider, {
    LinkComponent: LinkComponent,
    colorway: colorway,
    colorwayAgainst: colorwayAgainst,
    colorwaySecondary: colorwaySecondary,
    externalRoutes: externalRoutes,
    languageMap: languageMap,
    onLanguageChange: onLanguageChange,
    children: /*#__PURE__*/jsx(Header$1, {
      links: links
    })
  });
};
SalesHeader.displayName = 'SalesHeader';
var SalesHeader$1 = SalesHeader;

export { Footer$1 as F, GlyphAlignment as G, HeaderColorways as H, SalesHeader$1 as S, useContactLinks as a, useSupplementaryLinks as b, useShortcuts as c, useIsEcommerce as d, useHeaderHeightState$1 as e, useLoginUrl as f, useRegisterUrl as g, useHelpUrl as h, useAccountUrl as i, useAccountLinks as j, useHasAccount as k, useLogos as l, useHomeUrl as m, getCurrencyPickerLinks as n, hasCurrencyPicker as o, getCurrentCurrency as p, useSetLanguage as q, useBasketCount as r, useMenuItemActiveIndex as s, useShowMenu as t, useBookends as u, useBasketUrl as v, useLocation as w, useRefreshBasketCount as x, useBasket as y };
